import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle, Camera, Quotes, Star } from "@phosphor-icons/react";
import Nav from "@/components/site/Nav";
import Testimonials from "@/components/site/Testimonials";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DISPLAY_OPTIONS = [
  { value: "first", label: "First name only" },
  { value: "first_last_initial", label: "First name and last initial" },
  { value: "full", label: "Full name" },
  { value: "anonymous", label: "Anonymous" },
];

const GOAL_OPTIONS = [
  { value: "strength_muscle", label: "Strength and muscle building" },
  { value: "weight_loss_composition", label: "Weight loss or body composition" },
  { value: "confidence_gym", label: "Confidence in the gym" },
  { value: "accountability_consistency", label: "Accountability and consistency" },
  { value: "proper_form", label: "Learning proper form" },
  { value: "nutrition_habits", label: "Nutrition and healthier habits" },
  { value: "injury_limitation", label: "Training around an injury or limitation" },
  { value: "general_health", label: "General health and fitness" },
  { value: "other", label: "Other" },
];

const APPROVAL_OPTIONS = [
  { value: "yes_send_first", label: "Yes, please send it to me first" },
  { value: "no_publish_when_ready", label: "No, AJ may publish the approved submission" },
];

const INITIAL = {
  first_name: "",
  last_initial: "",
  display_preference: "first",
  location: "",
  email: "",
  duration: "",
  goals: [],
  quote: "",
  what_changed: "",
  recommendation: "",
  testimonial_permission: false,
  editing_permission: false,
  photo_permission: false,
  approval_preference: "yes_send_first",
  private_notes: "",
};

const inputClass = "w-full mt-2 bg-[#0F0F0F] border border-white/15 focus:border-[#FF1B91] focus:outline-none px-4 py-3 text-white placeholder:text-white/30 text-sm";
const labelClass = "block text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold";
const helpClass = "text-white/45 text-xs mt-1 leading-relaxed";

export default function Testimonial() {
  const [form, setForm] = useState(INITIAL);
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const filePreview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

  useEffect(() => () => { if (filePreview) URL.revokeObjectURL(filePreview); }, [filePreview]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleGoal = (value) => {
    setForm((f) => ({
      ...f,
      goals: f.goals.includes(value) ? f.goals.filter((g) => g !== value) : [...f.goals, value],
    }));
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      setError("Photo is over 10MB — please pick a smaller image.");
      return;
    }
    setError("");
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.first_name.trim() || !form.email.trim() || !form.quote.trim()) {
      setError("Please fill in your first name, email, and experience.");
      return;
    }
    if (!form.testimonial_permission || !form.editing_permission) {
      setError("The two permission checkboxes are required to submit.");
      return;
    }
    setStatus("sending");
    try {
      const body = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === "goals") body.append("goals", (v || []).join(","));
        else if (typeof v === "boolean") body.append(k, v ? "true" : "false");
        else body.append(k, v ?? "");
      });
      if (file) body.append("photo", file);
      const r = await fetch(`${BACKEND}/api/testimonials`, { method: "POST", body });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `Server returned ${r.status}`);
      }
      setStatus("done");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setStatus("error");
      setError(err.message || "Something glitched — please try again.");
    }
  };

  if (status === "done") {
    return (
      <main data-testid="testimonial-thanks" className="bg-[#0A0A0A] text-white font-body min-h-screen">
        <Nav />
        <div className="max-w-lg mx-auto px-6 pt-40 pb-24 text-center">
          <CheckCircle size={64} weight="fill" className="text-[#FF1B91] mx-auto mb-6" />
          <h1 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tighter mb-4">
            Thank <span className="font-brush text-[#FF1B91] -rotate-2 inline-block">you</span>
          </h1>
          <p className="text-white/75 leading-relaxed mb-8">
            I personally review every submission before it goes up. You&rsquo;ll see your testimonial live once I approve it{form.approval_preference === "yes_send_first" ? " — and I&rsquo;ll email you a preview first." : "."}
          </p>
          <Link to="/" data-testid="thanks-back-home" className="inline-flex items-center gap-3 border border-white/20 hover:border-[#FF1B91] hover:text-[#FF1B91] px-6 py-3 text-xs font-bold uppercase tracking-[0.2em]">
            <ArrowLeft size={14} weight="bold" /> Back home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="testimonial-page" className="bg-[#0A0A0A] text-white font-body min-h-screen">
      <Nav />

      {/* Existing approved testimonials — moved here from the landing page */}
      <div className="pt-28">
        <Testimonials />
      </div>

      {/* Submission form */}
      <section data-testid="testimonial-form-section" className="px-6 py-20 md:py-28 max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <span className="h-px w-10 bg-[#FF1B91]" />
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#FF1B91] font-semibold">Share your win</p>
        </div>
        <h1 className="font-display font-black uppercase text-4xl md:text-6xl tracking-tighter leading-[0.9] mb-6">
          Share Your <span className="font-brush font-normal normal-case text-[#FF1B91] -rotate-2 inline-block">Experience</span>
        </h1>
        <p className="text-white/75 leading-relaxed mb-3 text-lg">
          I&rsquo;d love to hear about your personal training experience with me. Share a few honest sentences about the progress you made, the support you received, or what stood out most about working together.
        </p>
        <p className="text-white/55 leading-relaxed mb-12 text-sm italic">
          Every submission is personally reviewed and approved by me before anything is shared.
        </p>

        <form onSubmit={submit} className="space-y-12">
          {/* ---------- About You ---------- */}
          <fieldset className="space-y-6">
            <legend className="font-display font-black uppercase text-2xl tracking-tight text-white mb-2">About You</legend>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <label className={labelClass}>
                First name <span className="text-[#FF1B91]">*</span>
                <input data-testid="input-first-name" required value={form.first_name} onChange={(e) => update({ first_name: e.target.value })} placeholder="Your first name" className={inputClass} />
              </label>
              <label className={labelClass}>
                Last initial <span className="text-white/40 normal-case tracking-normal font-normal ml-1">(optional)</span>
                <input data-testid="input-last-initial" value={form.last_initial} onChange={(e) => update({ last_initial: e.target.value })} placeholder="E.g. S" className={inputClass} maxLength={4} />
              </label>
            </div>

            <div>
              <p className={labelClass}>How would you like your name displayed? <span className="text-[#FF1B91]">*</span></p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                {DISPLAY_OPTIONS.map((o) => (
                  <label key={o.value} data-testid={`display-${o.value}`} className={`flex items-center gap-3 border px-4 py-3 cursor-pointer transition-colors ${form.display_preference === o.value ? "border-[#FF1B91] bg-[#FF1B91]/5" : "border-white/15 hover:border-white/30"}`}>
                    <input type="radio" name="display" value={o.value} checked={form.display_preference === o.value} onChange={() => update({ display_preference: o.value })} className="accent-[#FF1B91]" />
                    <span className="text-white text-sm">{o.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <label className={labelClass}>
                Location <span className="text-white/40 normal-case tracking-normal font-normal ml-1">(optional)</span>
                <input data-testid="input-location" value={form.location} onChange={(e) => update({ location: e.target.value })} placeholder="City, State" className={inputClass} />
              </label>
              <label className={labelClass}>
                Email address <span className="text-[#FF1B91]">*</span>
                <input data-testid="input-email" type="email" required value={form.email} onChange={(e) => update({ email: e.target.value })} placeholder="you@example.com" className={inputClass} />
                <span className={helpClass}>Used only if I need to follow up. Your email will never be published.</span>
              </label>
            </div>
          </fieldset>

          {/* ---------- Training Experience ---------- */}
          <fieldset className="space-y-6 pt-4 border-t border-white/10">
            <legend className="font-display font-black uppercase text-2xl tracking-tight text-white mb-2">Your Training Experience</legend>

            <label className={labelClass}>
              Approximately how long did we train together?
              <input data-testid="input-duration" value={form.duration} onChange={(e) => update({ duration: e.target.value })} placeholder="Example: Six months, one year, on and off for several years" className={inputClass} />
            </label>

            <div>
              <p className={labelClass}>What were your main fitness goals? <span className="text-white/40 normal-case tracking-normal font-normal ml-1">(select all that apply)</span></p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                {GOAL_OPTIONS.map((g) => {
                  const checked = form.goals.includes(g.value);
                  return (
                    <label key={g.value} data-testid={`goal-${g.value}`} className={`flex items-center gap-3 border px-4 py-3 cursor-pointer transition-colors ${checked ? "border-[#FF1B91] bg-[#FF1B91]/5" : "border-white/15 hover:border-white/30"}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleGoal(g.value)} className="accent-[#FF1B91]" />
                      <span className="text-white text-sm">{g.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <label className={labelClass}>
              Tell me about your experience working with me. <span className="text-[#FF1B91]">*</span>
              <textarea data-testid="input-quote" required rows={5} value={form.quote} onChange={(e) => update({ quote: e.target.value })} placeholder="What stood out about my coaching, support, accountability, energy, knowledge, or approach?" className={inputClass} />
            </label>

            <label className={labelClass}>
              What changed for you through training?
              <textarea data-testid="input-what-changed" rows={4} value={form.what_changed} onChange={(e) => update({ what_changed: e.target.value })} placeholder="Physical progress, increased confidence, stronger habits, better consistency, improved knowledge, or a healthier relationship with fitness." className={inputClass} />
            </label>

            <label className={labelClass}>
              What would you tell someone considering training with me?
              <textarea data-testid="input-recommendation" rows={3} value={form.recommendation} onChange={(e) => update({ recommendation: e.target.value })} className={inputClass} />
            </label>
          </fieldset>

          {/* ---------- Progress Photo ---------- */}
          <fieldset className="space-y-3 pt-4 border-t border-white/10">
            <legend className="font-display font-black uppercase text-2xl tracking-tight text-white mb-2">Progress Photo</legend>
            <p className="text-white/60 text-sm">Upload a progress or training photo <span className="text-white/40">(optional)</span>. You may upload a before-and-after photo, a single progress photo, or a photo from your training journey.</p>
            <input ref={fileRef} type="file" accept="image/*" data-testid="input-photo" onChange={onFile} className="hidden" />
            <div className="flex items-center gap-4">
              <button type="button" data-testid="photo-pick" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-3 border border-white/20 hover:border-[#FF1B91] hover:text-[#FF1B91] text-white px-5 py-3 text-[11px] font-bold uppercase tracking-[0.2em]">
                <Camera size={14} weight="bold" /> {file ? "Change photo" : "Upload photo"}
              </button>
              {file && (
                <button type="button" data-testid="photo-remove" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-xs text-white/50 hover:text-white underline">
                  Remove
                </button>
              )}
            </div>
            {filePreview && (
              <img src={filePreview} alt="Preview" data-testid="photo-preview" className="mt-3 border border-white/15 max-h-64 object-contain" />
            )}
          </fieldset>

          {/* ---------- Permissions ---------- */}
          <fieldset className="space-y-4 pt-4 border-t border-white/10">
            <legend className="font-display font-black uppercase text-2xl tracking-tight text-white mb-2">Permission to Share</legend>

            <label data-testid="perm-testimonial" className={`flex items-start gap-3 border px-4 py-4 cursor-pointer transition-colors ${form.testimonial_permission ? "border-[#FF1B91] bg-[#FF1B91]/5" : "border-white/15 hover:border-white/30"}`}>
              <input type="checkbox" required checked={form.testimonial_permission} onChange={(e) => update({ testimonial_permission: e.target.checked })} className="accent-[#FF1B91] mt-1" />
              <span className="text-white text-sm leading-relaxed">
                <strong className="uppercase tracking-[0.15em] text-[11px] block mb-1">Testimonial permission <span className="text-[#FF1B91]">*</span></strong>
                I give AJ and Sweat With Slay permission to share my testimonial on the Sweat With Slay website, social media, marketing materials, and related promotional content.
              </span>
            </label>

            <label data-testid="perm-editing" className={`flex items-start gap-3 border px-4 py-4 cursor-pointer transition-colors ${form.editing_permission ? "border-[#FF1B91] bg-[#FF1B91]/5" : "border-white/15 hover:border-white/30"}`}>
              <input type="checkbox" required checked={form.editing_permission} onChange={(e) => update({ editing_permission: e.target.checked })} className="accent-[#FF1B91] mt-1" />
              <span className="text-white text-sm leading-relaxed">
                <strong className="uppercase tracking-[0.15em] text-[11px] block mb-1">Editing permission <span className="text-[#FF1B91]">*</span></strong>
                I understand that my testimonial may be lightly edited for spelling, grammar, clarity, or length without changing the meaning of my statement.
              </span>
            </label>

            <label data-testid="perm-photo" className={`flex items-start gap-3 border px-4 py-4 cursor-pointer transition-colors ${form.photo_permission ? "border-[#FF1B91] bg-[#FF1B91]/5" : "border-white/15 hover:border-white/30"}`}>
              <input type="checkbox" checked={form.photo_permission} onChange={(e) => update({ photo_permission: e.target.checked })} className="accent-[#FF1B91] mt-1" />
              <span className="text-white text-sm leading-relaxed">
                <strong className="uppercase tracking-[0.15em] text-[11px] block mb-1">Photo permission</strong>
                I give AJ and Sweat With Slay permission to share the photo or photos I uploaded.
              </span>
            </label>

            <div>
              <p className={labelClass}>Would you like to approve the final version before it is posted?</p>
              <div className="grid grid-cols-1 gap-2 mt-2">
                {APPROVAL_OPTIONS.map((o) => (
                  <label key={o.value} data-testid={`approval-${o.value}`} className={`flex items-center gap-3 border px-4 py-3 cursor-pointer transition-colors ${form.approval_preference === o.value ? "border-[#FF1B91] bg-[#FF1B91]/5" : "border-white/15 hover:border-white/30"}`}>
                    <input type="radio" name="approval" value={o.value} checked={form.approval_preference === o.value} onChange={() => update({ approval_preference: o.value })} className="accent-[#FF1B91]" />
                    <span className="text-white text-sm">{o.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <label className={labelClass}>
              Is there anything you would like kept private? <span className="text-white/40 normal-case tracking-normal font-normal ml-1">(optional)</span>
              <textarea data-testid="input-private-notes" rows={2} value={form.private_notes} onChange={(e) => update({ private_notes: e.target.value })} className={inputClass} />
            </label>
          </fieldset>

          {/* ---------- Submit ---------- */}
          <div className="pt-4 border-t border-white/10">
            {error && <p data-testid="form-error" className="text-[#FF1B91] text-sm mb-4">{error}</p>}
            <button data-testid="testimonial-submit" type="submit" disabled={status === "sending"} className="w-full bg-[#FF1B91] hover:bg-[#FF3FA3] disabled:opacity-60 text-white px-8 py-5 text-sm font-bold uppercase tracking-[0.25em] btn-brutal btn-brutal-pink inline-flex items-center justify-center gap-3">
              {status === "sending" ? "Sending…" : <>Share My Experience <ArrowRight size={16} weight="bold" /></>}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}
